import { ForwardedRef, forwardRef, memo, useCallback, useImperativeHandle, useMemo, useRef, useState, ReactElement } from 'react';
import { EditOutlined, DeleteOutlined, EyeOutlined, HistoryOutlined } from '@ant-design/icons';
import { Button, Flex, Space, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import { Table, TableComponent, TableProps } from '@ohpen/enso-ui';
import { Guard, useGuard } from '@ohpen/react-authz';
import { ActionEntityType, ProcurationFeeItem, ProcurationFeeItemsResponse } from '@pmp/api';
import { useListTableState } from '@pmp/hooks';
import { lockActionTitle } from '@pmp/utils';
import { ListProps } from '@pmp/types';

import { procurationFeeColumns, procurationFeeFeesColumns, procurationFeeFlatColumns } from '../tableConfig';
import { ProcurationFeesExport, ProcurationFeesListComponent } from '../interfaces';

const entityType = ActionEntityType.PROCURATION_FEES;
const rowKey = 'id';

const ProcurationFeesListBase = (
  {
    data,
    isFetching,
    valueLists,
    onCreate,
    onView,
    onEdit,
    onDelete,
    onViewChangeLog,
    onBulkDelete,
  }: ListProps<ProcurationFeeItemsResponse> & {
    onBulkDelete: (records: ProcurationFeeItemsResponse[]) => void;
  },
  externalRef: ForwardedRef<ProcurationFeesListComponent>,
): ReactElement => {
  const tableRef = useRef<TableComponent | null>(null);
  const [selectedRows, setSelectedRows] = useState<ProcurationFeeItemsResponse[]>([]);
  const { allowed: showRowSelection } = useGuard({
    permissions: ['procurationFees:delete'],
    flags: ['enableProcurationFeesCrud'],
  });
  const { t } = useTranslation();
  const tableState = useListTableState<ProcurationFeeItemsResponse>(entityType, rowKey, tableRef, procurationFeeColumns);

  const onChangeSelectedRows = useCallback(
    (selectedRows: ProcurationFeeItemsResponse[]) => {
      setSelectedRows(selectedRows);
      tableState.onChangeSelectedRows?.(selectedRows);
    },
    [tableState],
  );

  // The table clears its own selection without calling back, so the mirrored state is cleared here too.
  const resetSelectedRows = useCallback(() => {
    tableRef.current?.resetSelectedRows();
    onChangeSelectedRows([]);
  }, [onChangeSelectedRows]);

  useImperativeHandle(externalRef, () => ({ resetSelectedRows }));

  const disabledBulkDelete: boolean = useMemo(() => selectedRows.some(record => !!record.lockedByActionId), [selectedRows]);

  const getRowActions: (record: ProcurationFeeItemsResponse) => ReactElement[] = useCallback(
    record => {
      const rowActions = [
        <Tooltip key="view" title={t('global.buttons.view')} placement="top">
          <Button type="text" aria-label="View Procuration Fee" icon={<EyeOutlined />} onClick={() => onView(record)} />
        </Tooltip>,
        <Guard key="edit" permissions={['procurationFees:update']} flags={['enableProcurationFeesCrud']}>
          <Tooltip title={lockActionTitle('edit', record.lockedByActionId)} placement="top">
            <Button
              type="text"
              aria-label="Edit Procuration Fee"
              icon={<EditOutlined key="edit" />}
              onClick={() => onEdit(record)}
              disabled={!!record.lockedByActionId}
            />
          </Tooltip>
        </Guard>,
        <Guard key="delete" permissions={['procurationFees:delete']} flags={['enableProcurationFeesCrud']}>
          <Tooltip title={lockActionTitle('delete', record.lockedByActionId)} placement="top">
            <Button
              type="text"
              aria-label="Delete Procuration Fee"
              icon={<DeleteOutlined key="delete" />}
              onClick={() => onDelete(record)}
              disabled={!!record.lockedByActionId}
            />
          </Tooltip>
        </Guard>,
        <Guard key="changeLog" flags={['enableChangeLog']}>
          <Tooltip title={t('lender.changeLog.labels.viewChangeLog')} placement="top">
            <Button type="text" aria-label="View change log" icon={<HistoryOutlined />} onClick={() => onViewChangeLog?.(record)} />
          </Tooltip>
        </Guard>,
      ];

      return rowActions;
    },
    [onDelete, onEdit, onView, onViewChangeLog, t],
  );

  const tableProps: TableProps<
    ActionEntityType,
    ProcurationFeeItemsResponse,
    ProcurationFeeItem,
    ProcurationFeeItemsResponse,
    ProcurationFeesExport
  > = useMemo(
    () => ({
      ...tableState,
      id: entityType,
      itemsLabel: t('lender.procurationFees.labels.procurationFees'),
      ref: tableRef,
      dataSource: data ?? [],
      columns: procurationFeeColumns,
      exportColumns: procurationFeeFlatColumns,
      rowKey,
      pagination: true,
      scroll: { x: 'max-content', y: '100%' },
      valueLists,
      loading: isFetching,
      getRowActions,
      expanded: {
        columns: procurationFeeFeesColumns,
        columnsKey: 'feeItems',
        rowKey: 'index',
      },
      showRowSelection,
      onChangeSelectedRows,
      showViews: false,
    }),
    [tableState, t, data, valueLists, isFetching, getRowActions, showRowSelection, onChangeSelectedRows],
  );

  return (
    <Table {...tableProps}>
      <>
        <Table.ActionsBar>
          <Flex justify="space-between">
            <Space size="middle">
              <Table.GlobalSearch />
            </Space>
            <Space size="middle">
              <Table.ResetFiltersButton />
              <Table.SelectColumnsButton />
              <Guard permissions={['procurationFees:create']} flags={['enableProcurationFeesCrud']}>
                <Table.CreateButton onClick={onCreate} />
              </Guard>
            </Space>
          </Flex>
        </Table.ActionsBar>
        <Table.BulkBar xItemsSelectedLabel="lender.procurationFees.labels.xProcurationFeesSelected">
          <Space size="middle">
            <Table.BulkDeleteButton
              onClick={onBulkDelete}
              disabled={disabledBulkDelete}
              tooltip={disabledBulkDelete ? t('global.labels.bulkDeleteLockedByChangeRequest') : undefined}
            />
          </Space>
        </Table.BulkBar>
      </>
    </Table>
  );
};

export const ProcurationFeesList = memo(forwardRef(ProcurationFeesListBase));
